import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import api from "../lib/api";
import useSeo, { getArticleSchema } from "../hooks/useSeo";
import { ChevronRight, Calendar, User, ArrowLeft } from "lucide-react";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => {});
    api.get("/blog", { params: { limit: 4 } }).then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3)));
  }, [slug]);

  useSeo({
    title: post ? post.seo_title || `${post.title_tr} — Otel Pro Blog` : "Otel Pro Blog",
    description: post?.seo_description || post?.excerpt,
    image: post?.cover_image,
    ogType: "article",
    jsonLd: post ? getArticleSchema(post, window.location.origin) : null,
  });

  if (!post) return <Layout><div className="section-pad container-site">Yükleniyor...</div></Layout>;

  return (
    <Layout>
      <div className="bg-softbg">
        <div className="container-site py-5 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <Link to="/" className="hover:text-navy">Ana Sayfa</Link>
          <ChevronRight size={12} />
          <Link to="/blog" className="hover:text-navy">Blog</Link>
          <ChevronRight size={12} />
          <span className="text-navy font-medium line-clamp-1">{post.title_tr}</span>
        </div>
      </div>

      <section className="relative bg-navy text-white py-20"
        style={post.cover_image ? { backgroundImage: `linear-gradient(rgba(8,17,29,0.8), rgba(8,17,29,0.7)), url(${post.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
      >
        <div className="container-site max-w-3xl">
          <p className="accent-script text-gold text-2xl mb-2">Blog</p>
          <h1 className="heading-serif text-3xl md:text-5xl font-semibold leading-tight">{post.title_tr}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-white/70">
            <span className="flex items-center gap-1"><User size={12} /> {post.author}</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> {(post.published_at || "").slice(0, 10)}</span>
            {post.tags?.map((t) => (
              <span key={t} className="bg-white/10 px-2 py-0.5 rounded-full">#{t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-site max-w-3xl">
          <article className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />

          <div className="mt-12 p-7 rounded-xl bg-navy text-white text-center">
            <p className="accent-script text-gold text-xl mb-1">Projeniz için</p>
            <h3 className="heading-serif text-2xl font-semibold mb-3">Uzman Danışmanlık Alın</h3>
            <Link to="/iletisim" className="btn-gold" data-testid="blog-cta">Ücretsiz Danışmanlık</Link>
          </div>

          <div className="mt-8">
            <Link to="/blog" className="inline-flex items-center gap-1 text-navy hover:text-gold" data-testid="blog-back">
              <ArrowLeft size={14} /> Tüm Yazılar
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-softbg section-pad !py-14">
          <div className="container-site max-w-5xl">
            <h3 className="heading-serif text-2xl font-semibold text-navy mb-6">İlgili Yazılar</h3>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="card-premium overflow-hidden group">
                  {p.cover_image && (
                    <div className="aspect-[16/10] bg-softbg overflow-hidden">
                      <img src={p.cover_image} alt={p.title_tr} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  )}
                  <div className="p-4">
                    <h4 className="heading-serif text-sm font-semibold text-navy group-hover:text-gold">{p.title_tr}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
